// src/utils/imprimerRecu.js
// ============================================================================
// IMPRIMER UN REÇU DE CAISSE
// ============================================================================
//
// Deux voies, réglées indépendamment par l'école (Paramétrage → Reçus et
// impression), décision de Daniel du 03/11/2026 :
//
//   B — l'agent KLASYS Impression posé sur le poste de caisse (lot 3) :
//       le serveur rend un document ESC/POS (services/RecuEscposService.js),
//       envoyé à http://127.0.0.1:<port>/imprimer. Pas de boîte d'impression,
//       papier coupé, tiroir-caisse ouvert. Passe en premier quand il est
//       activé.
//   A — le navigateur et le pilote Windows (lot 2) : le reçu en HTML, ouvert
//       dans un cadre invisible et imprimé. Sert de secours quand l'agent ne
//       répond pas sur ce poste.
//
// Le serveur rend les deux d'un seul appel : la sortie n'est comptée qu'une
// fois, même quand le navigateur prend le relais de l'agent.
//
// Le ticket 80 mm sort à la longueur du reçu : sa hauteur est mesurée ici,
// juste avant l'impression par le navigateur.
// ============================================================================

import { api } from '../services/api';

const MM_PAR_PX = 25.4 / 96;
const DUREE_CACHE_REGLAGES = 60 * 1000;

/**
 * Imprime un document HTML complet par le navigateur. Rend { longueurMm } :
 * le papier pris par un ticket 80 mm, exemplaires compris (null en A4), pour
 * le journal des reçus.
 */
export function imprimerHtml(html, { format = 'ticket80' } = {}) {
  let longueurMm = null;
  return new Promise((resolve, reject) => {
    const cadre = document.createElement('iframe');
    cadre.setAttribute('aria-hidden', 'true');
    cadre.setAttribute('tabindex', '-1');
    // Largeur réelle du papier : la hauteur mesurée en dépend.
    cadre.style.cssText = `position:fixed;left:-10000px;top:0;width:${format === 'a4' ? '210mm' : '80mm'};`
      + 'height:100px;border:0;opacity:0;pointer-events:none;';

    let fini = false;
    const terminer = (erreur) => {
      if (fini) return;
      fini = true;
      // Retirer le cadre trop tôt annule l'impression dans certains navigateurs.
      setTimeout(() => cadre.remove(), 1500);
      if (erreur) reject(erreur); else resolve({ longueurMm });
    };

    cadre.onload = () => {
      try {
        const doc = cadre.contentDocument;
        const fenetre = cadre.contentWindow;

        const lancer = () => {
          try {
            if (format !== 'a4') {
              const exemplaires = [...doc.querySelectorAll('.exemplaire')];
              const hauteurPx = exemplaires.length
                ? Math.max(...exemplaires.map((e) => e.getBoundingClientRect().height))
                : doc.body.scrollHeight;
              const hauteurMm = Math.ceil(hauteurPx * MM_PAR_PX) + 2;
              longueurMm = hauteurMm * Math.max(1, exemplaires.length);
              const style = doc.createElement('style');
              style.textContent = `@page { size: 80mm ${hauteurMm}mm; margin: 0; }`;
              doc.head.appendChild(style);
            }
            fenetre.onafterprint = () => terminer();
            fenetre.focus();
            fenetre.print();
            // Chrome rend la main quand la boîte d'impression se ferme.
            setTimeout(() => terminer(), 500);
          } catch (erreur) {
            terminer(erreur);
          }
        };

        // Le logo doit être chargé avant de mesurer et d'imprimer.
        const enAttente = [...doc.images].filter((img) => !img.complete);
        if (!enAttente.length) lancer();
        else {
          Promise.all(enAttente.map((img) => new Promise((ok) => { img.onload = ok; img.onerror = ok; })))
            .then(lancer);
        }
      } catch (erreur) {
        terminer(erreur);
      }
    };

    document.body.appendChild(cadre);
    cadre.srcdoc = html;
  });
}

// ----------------------------------------------------------------------------
// L'agent du poste
// ----------------------------------------------------------------------------

const adresseAgent = (port) => `http://127.0.0.1:${Number(port) || 4210}`;

/** L'agent de ce poste, ou null s'il ne répond pas. */
export async function sonderAgent(port, { delai = 1500 } = {}) {
  const controle = new AbortController();
  const minuteur = setTimeout(() => controle.abort(), delai);
  try {
    const reponse = await fetch(`${adresseAgent(port)}/etat`, { signal: controle.signal });
    if (!reponse.ok) return null;
    const etat = await reponse.json();
    return etat?.agent === 'klasys-impression' ? etat : null;
  } catch {
    return null;
  } finally {
    clearTimeout(minuteur);
  }
}

/** Envoie un document à l'agent. Lève si l'agent ne répond pas ou refuse. */
export async function envoyerAgent(port, document) {
  let reponse;
  try {
    reponse = await fetch(`${adresseAgent(port)}/imprimer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(document)
    });
  } catch {
    throw new Error("L'agent KLASYS ne répond pas sur ce poste : vérifiez l'icône K près de l'horloge.");
  }
  const corps = await reponse.json().catch(() => ({}));
  if (!reponse.ok || corps.ok === false) {
    throw new Error(corps.message || "L'agent KLASYS n'a pas pu imprimer le reçu.");
  }
  return corps;
}

/** Ouvre le tiroir-caisse par l'agent du poste (bouton « Essayer » des réglages). */
export async function ouvrirTiroirAgent(port) {
  let reponse;
  try {
    reponse = await fetch(`${adresseAgent(port)}/tiroir`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}'
    });
  } catch {
    throw new Error("L'agent KLASYS ne répond pas sur ce poste : vérifiez l'icône K près de l'horloge.");
  }
  const corps = await reponse.json().catch(() => ({}));
  if (!reponse.ok || corps.ok === false) throw new Error(corps.message || "L'agent n'a pas pu ouvrir le tiroir.");
  return corps;
}

/**
 * Le poste complète la ligne du journal des reçus (RecuJournalService) : la
 * voie qui a imprimé, ce que l'agent a répondu pour le tiroir, la longueur
 * mesurée. Sans attendre, et sans gêner la caisse s'il échoue : le journal
 * garde de toute façon la demande, notée par le serveur.
 */
export function rapporterSortie(sortieId, retour) {
  if (!sortieId) return;
  api.recusCaisse.retourSortie(sortieId, retour).catch(() => {});
}

/**
 * Imprime ce que le serveur a préparé ({ html, document, format, sortie_id })
 * par la bonne voie. Rend { voie, message } : `message` dit à la caisse ce
 * qui s'est passé quand ce n'est pas l'impression attendue (agent
 * injoignable, imprimante hors ligne). Le journal reçoit le retour du poste.
 */
export async function imprimerSortie(sortie, reglages) {
  // Le tiroir s'ouvre par l'agent seulement, quand le serveur l'a demandé.
  const tiroir = sortie.document?.tiroir ? 'ouvert' : null;
  const tiroirFerme = sortie.document?.tiroir ? 'non_ouvert' : null;
  const parNavigateur = async (messageAgent = null) => {
    try {
      const { longueurMm } = await imprimerHtml(sortie.html, { format: sortie.format });
      rapporterSortie(sortie.sortie_id, { voie: 'navigateur', tiroir: tiroirFerme, longueur_mm: longueurMm, message: messageAgent });
    } catch (erreur) {
      rapporterSortie(sortie.sortie_id, { voie: 'aucune', tiroir: tiroirFerme, message: erreur.message });
      throw erreur;
    }
  };

  if (sortie.document && reglages?.agent) {
    try {
      const resultat = await envoyerAgent(reglages.agent_port, sortie.document);
      const message = resultat.en_attente ? resultat.message : null;
      rapporterSortie(sortie.sortie_id, { voie: 'agent', tiroir, message });
      return { voie: 'agent', message };
    } catch (erreur) {
      if (!reglages.navigateur || !sortie.html) {
        rapporterSortie(sortie.sortie_id, { voie: 'aucune', tiroir: tiroirFerme, message: erreur.message });
        throw erreur;
      }
      await parNavigateur(erreur.message);
      return { voie: 'navigateur', message: "L'agent KLASYS ne répond pas sur ce poste : reçu imprimé par le navigateur." };
    }
  }
  await parNavigateur();
  return { voie: 'navigateur', message: null };
}

// ----------------------------------------------------------------------------
// Les réglages et le reçu
// ----------------------------------------------------------------------------

let reglagesEnCache = null;
let lusLe = 0;

/** Les réglages d'impression de l'école (une minute en cache), ou ceux par défaut. */
export async function lireReglagesImpression({ frais = false } = {}) {
  if (!frais && reglagesEnCache && Date.now() - lusLe < DUREE_CACHE_REGLAGES) return reglagesEnCache;
  const reponse = await api.recusCaisse.impression();
  reglagesEnCache = reponse?.success && reponse.data
    ? reponse.data
    : { format: 'ticket80', navigateur: true, agent: false, agent_port: 4210, impression_auto: false, exemplaires: 1 };
  lusLe = Date.now();
  return reglagesEnCache;
}

/** À appeler quand la page de réglages vient d'enregistrer. */
export function oublierReglagesImpression() {
  reglagesEnCache = null;
}

/**
 * Imprime un reçu émis. Le serveur compte la sortie : au-delà de la
 * première, le reçu sort en DUPLICATA.
 *
 * Rend { numero, duplicata, voie, message } ; lève avec un message lisible.
 */
export async function imprimerRecu(recuId, { format } = {}) {
  const reglages = await lireReglagesImpression();
  const pourAgent = Boolean(reglages.agent) && (format || reglages.format) !== 'a4';
  const reponse = await api.recusCaisse.imprimer(recuId, { format, pourAgent });
  if (!reponse?.success || !reponse.data?.html) {
    throw new Error(reponse?.message || "Le reçu n'a pas pu être préparé pour l'impression.");
  }
  const { voie, message } = await imprimerSortie(reponse.data, reglages);
  return { numero: reponse.data.numero, duplicata: Boolean(reponse.data.duplicata), voie, message };
}

/** L'école a-t-elle réglé l'impression automatique ? (réglages en cache) */
export async function impressionAutomatique() {
  try {
    return Boolean((await lireReglagesImpression()).impression_auto);
  } catch {
    return false;
  }
}

/**
 * Après un encaissement : le reçu émis part à l'imprimante, sans attendre.
 * `surMessage(message, type)` reçoit ce que la caisse doit savoir (agent
 * injoignable, échec d'impression). À n'appeler que si impressionAutomatique().
 */
export function imprimerApresEncaissement(recu, surMessage) {
  if (!recu?.id) return;
  imprimerRecu(recu.id)
    .then(({ message }) => { if (message) surMessage?.(message, 'info'); })
    .catch((e) => surMessage?.(e.message || "Le reçu n'a pas pu être imprimé", 'error'));
}

export default imprimerRecu;
